import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl} from 'react-native';
import {
  Text, Card, Button, Chip, Appbar, ActivityIndicator,
  Dialog, Portal, Divider,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {userApi} from '../../services/api/userApi';
import {colors, spacing} from '../../theme';

const DetailRow = ({label, value}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <Text variant="bodySmall" style={styles.detailLabel}>{label}</Text>
      <Text variant="bodyMedium" style={styles.detailValue}>{value}</Text>
    </View>
  );
};

const UserDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {userId} = route.params;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deactivateVisible, setDeactivateVisible] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await userApi.getById(userId);
      setUser(res.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, t]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleDeactivate = useCallback(async () => {
    setDeactivateVisible(false);
    try {
      await userApi.delete(userId);
      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, navigation, t]);

  const handleReactivate = useCallback(async () => {
    try {
      await userApi.reactivate(userId);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [userId, fetchData, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={user?.fullName || ''} titleStyle={styles.appbarTitle} />
        <Appbar.Action
          icon="pencil"
          onPress={() => navigation.navigate('UserForm', {userId})}
          color={colors.white}
        />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {user && (
          <>
            {/* Profile Card */}
            <Card style={styles.infoCard}>
              <Card.Content style={styles.infoRow}>
                <View style={styles.avatarSection}>
                  <View style={styles.avatarFallback}>
                    <Text style={styles.avatarText}>{(user.fullName || '?').charAt(0).toUpperCase()}</Text>
                  </View>
                </View>
                <View style={styles.infoSection}>
                  <Text variant="headlineSmall" style={styles.name}>{user.fullName}</Text>
                  {user.email && <Text variant="bodyMedium" style={styles.subtext}>{user.email}</Text>}
                  {user.phone && <Text variant="bodyMedium" style={styles.subtext}>{user.phone}</Text>}
                  <View style={styles.chipRow}>
                    <Chip
                      style={[styles.statusChip, {backgroundColor: user.isActive ? colors.success + '20' : colors.error + '20'}]}>
                      <Text style={{fontSize: 13, color: user.isActive ? colors.success : colors.error, marginTop: 3}}>
                        {user.isActive ? t('user.active') : t('user.inactive')}
                      </Text>
                    </Chip>
                    {user.isOwner && (
                      <Chip style={styles.ownerChip}>
                        <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t('user.owner')}</Text>
                      </Chip>
                    )}
                  </View>
                </View>
              </Card.Content>
            </Card>

            {/* Details Card */}
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Text variant="titleMedium" style={styles.sectionTitle}>{t('user.details')}</Text>
                <DetailRow label={t('user.email')} value={user.email} />
                <DetailRow label={t('user.phone')} value={user.phone} />
                <DetailRow label={t('user.notes')} value={user.notes} />
                {user.linkedWorkerName && (
                  <DetailRow label={t('user.linkedWorker')} value={user.linkedWorkerName} />
                )}
              </Card.Content>
            </Card>

            {/* Org & Role Assignments Card */}
            {user.orgRoleAssignments && user.orgRoleAssignments.length > 0 && (
              <Card style={styles.sectionCard}>
                <Card.Content>
                  <Text variant="titleMedium" style={styles.sectionTitle}>{t('user.orgRoleAssignments')}</Text>
                  {user.orgRoleAssignments.map((a, idx) => (
                    <View key={`${a.orgUnitId}-${a.roleId}-${idx}`} style={styles.assignmentRow}>
                      <View style={styles.assignmentInfo}>
                        <Text variant="bodyMedium" style={styles.assignmentStore}>{a.orgUnitName}</Text>
                        <Text variant="bodySmall" style={styles.assignmentRole}>{a.roleName}</Text>
                      </View>
                      {a.isPrimary && (
                        <Chip style={styles.primaryChip} textStyle={{fontSize: 11}}>
                          {t('user.primary')}
                        </Chip>
                      )}
                    </View>
                  ))}
                </Card.Content>
              </Card>
            )}

            {/* Action Buttons */}
            {!user.isOwner && (
              <View style={styles.actionRow}>
                {user.isActive ? (
                  <Button
                    mode="outlined"
                    icon="account-off"
                    textColor={colors.error}
                    style={styles.deactivateButton}
                    onPress={() => setDeactivateVisible(true)}>
                    {t('user.deactivate')}
                  </Button>
                ) : (
                  <Button
                    mode="outlined"
                    icon="account-check"
                    textColor={colors.success}
                    style={styles.reactivateButton}
                    onPress={handleReactivate}>
                    {t('user.reactivate')}
                  </Button>
                )}
              </View>
            )}
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('user.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('user.deactivateMessage', {name: user?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('user.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('user.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {snackbar ? (
        <View style={styles.snackbarContainer}>
          <Text style={styles.snackbarText}>{snackbar}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  infoCard: {margin: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  infoRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  avatarSection: {width: '25%', alignItems: 'center'},
  avatarFallback: {
    width: 72, height: 72, borderRadius: 36, backgroundColor: colors.secondary,
    justifyContent: 'center', alignItems: 'center',
  },
  avatarText: {color: colors.white, fontSize: 28, fontWeight: 'bold'},
  infoSection: {flex: 1},
  name: {fontWeight: 'bold', color: colors.text},
  subtext: {color: colors.textSecondary, marginTop: 2},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs},
  statusChip: {alignSelf: 'flex-start', height: 26, paddingVertical: 0},
  ownerChip: {alignSelf: 'flex-start', backgroundColor: colors.warning + '20'},
  sectionCard: {marginHorizontal: spacing.md, marginBottom: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  sectionTitle: {fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  detailRow: {paddingVertical: spacing.xs, borderBottomWidth: 1, borderBottomColor: colors.divider},
  detailLabel: {color: colors.textSecondary, fontSize: 12},
  detailValue: {color: colors.text, marginTop: 2},
  assignmentRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  assignmentInfo: {flex: 1},
  assignmentStore: {fontWeight: '600', color: colors.text},
  assignmentRole: {color: colors.textSecondary, marginTop: 2},
  primaryChip: {backgroundColor: colors.primary + '15', height: 24},
  actionRow: {marginHorizontal: spacing.md, marginBottom: spacing.xl},
  deactivateButton: {borderColor: colors.error},
  reactivateButton: {borderColor: colors.success},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
  snackbarContainer: {
    position: 'absolute', bottom: spacing.md, left: spacing.md, right: spacing.md,
    backgroundColor: '#333', borderRadius: 8, padding: spacing.md,
  },
  snackbarText: {color: '#fff', fontSize: 14},
});

export default UserDetailScreen;
